import PropTypes from 'prop-types';
import { createContext, useContext, useRef, useState } from 'react';
import { useParams } from 'react-router';

import { ErrorBoundaryError } from 'errorBoundary/errors';
import { HCAFE_COMPANY_ID, PRICING_PLANS } from 'constants/chownow';

import useCompany from 'hooks/useCompany';
import useLocation, { STATUSES } from 'hooks/useLocation';

import { AuthContext } from './AuthContext';

export const HeadquarterContext = createContext();

export function HeadquarterProvider({ children }) {
  const { user, isInternalUser, isUserCubohOnly, isInitialLoad, merchants, companies } = useContext(AuthContext);
  const { companyId, restaurantId } = useParams();
  // capture hqId from path so that an internal user can navigate around
  // potentially dropping the hqId from the URL and still maintain the HQ
  const [internalUserImpliedHqId, setInternalUserImpliedHqId] = useState(companyId);
  const [selectedLocationId, setSelectedLocationId] = useState(restaurantId);
  // last location id confirmed unauthorized — prevents re-select/reset loop (CN-49639)
  const unauthorizedLocationIdRef = useRef();

  const isDisabledDueToCubohLocation = !!restaurantId?.startsWith('cuboh-');

  // this is the location as specified by the URL bar
  const {
    location: urlLocation,
    status: urlLocationStatus,
    refreshLocation,
    silentRefreshLocation,
  } = useLocation(selectedLocationId, {
    // never fetch a CN location for a cuboh-only user
    enabled: !isUserCubohOnly && !isDisabledDueToCubohLocation,
  });

  if (isDisabledDueToCubohLocation && isInternalUser) {
    throw new ErrorBoundaryError({
      errorDetails: {
        subtitle: 'ChowNow admins should impersonate through Corpa to see Cuboh-only locations.',
      },
    });
  }

  const internalUserHqId = urlLocation?.company_id || companyId || HCAFE_COMPANY_ID;

  const hqId = isInternalUser ? internalUserHqId : urlLocation?.company_id || user?.company_id;

  const [headquarter, headquarterLocations, isHqLoading, refreshCompanyLocations] = useCompany(
    hqId,
    {
      // do not fetch the company until the restaurant loads (or load it if
      // there is no restoId) in the query params
      enabled:
        !restaurantId ||
        !!urlLocation ||
        urlLocationStatus === STATUSES.ERRORED ||
        urlLocationStatus === STATUSES.UNAUTHORIZED,
    },
  );

  const locationsWithPremierPlan = headquarterLocations.filter(
    loc => loc.product_plan_group === PRICING_PLANS.premier,
  );

  const locationId =
    selectedLocationId || locationsWithPremierPlan[0]?.id || headquarterLocations[0]?.id;
  const locationFromCompany = (headquarter?.locations || []).find(loc => loc.id === locationId);

  if (user) {
    if (companyId && companyId !== internalUserImpliedHqId) {
      // if the companyId changes in the URL, update the internal hq ID & reset the location

      setSelectedLocationId(undefined);
      setInternalUserImpliedHqId(companyId);
      unauthorizedLocationIdRef.current = undefined;
    } else if (restaurantId && selectedLocationId !== restaurantId) {
      // if the :restaurantId param in the URL changes, update the internal location id

      setSelectedLocationId(restaurantId);
    } else if (!restaurantId && selectedLocationId && urlLocationStatus === STATUSES.UNAUTHORIZED) {
      // if the user navigates away from a restaurant route (e.g. to /) while the selected
      // location is unauthorized, reset so the stale unauthorized state doesn't persist

      unauthorizedLocationIdRef.current = selectedLocationId;
      setSelectedLocationId(undefined);
    } else if (
      !restaurantId &&
      !selectedLocationId &&
      locationId &&
      locationId !== unauthorizedLocationIdRef.current &&
      (!companyId || companyId === headquarter?.id)
    ) {
      // if there is no location id in the URL (i.e. on /), we have not previously
      // selected a location, there is a location available (i.e. from the HQ), &
      // either there is no hqId in the params (internal user) or the proper HQ is actually loaded
      // select a location id

      setSelectedLocationId(locationId);
    }
  }

  if ((selectedLocationId || internalUserImpliedHqId) && !user && !isInitialLoad) {
    // if the user signs out, reset all state

    setSelectedLocationId(undefined);
    setInternalUserImpliedHqId(undefined);
    unauthorizedLocationIdRef.current = undefined;
  }

  const compositeLocation = {
    // we want to build a composite location that with some information from the company
    // so that if the urlLocation call fails (the API returns an error), we can show
    // the name of the selected location in the LocationSelector and NavBar.
    ...locationFromCompany,
    // is_live always comes from the lookup, so the company list can't fill in a stale value
    ...(urlLocation &&
      urlLocation.id === selectedLocationId && { ...urlLocation, is_live: urlLocation.is_live }),
  };

  const location = Object.keys(compositeLocation).length === 0 ? undefined : compositeLocation;

  // we have a computed loading state for the location as it depends on multiple state values
  const isLocationLoading =
    // cuboh users do not use anything HQ context so it's never loading
    !isUserCubohOnly &&
    (urlLocationStatus === 'loading' ||
      // on navigation, show a loading state ahead of `useLocation` updating
      (!!urlLocation && urlLocation.id && urlLocation.id !== locationId) ||
      (!!restaurantId && restaurantId !== selectedLocationId));

  // non-internal users should only access their own company
  const isCompanyUnauthorized =
    !isInternalUser && !!companyId && !!user && String(companyId) !== String(user?.company_id);

  // For Cuboh-integrated locations: find the Cuboh company and count its merchants.
  // Cuboh URLs use "cuboh-{cubohCompanyId}" — the number IS the Cuboh company ID,
  // which is also the key in the companies map. For ChowNow locations, match by
  // chownow_restaurant_id. Both paths are needed: location?.id is undefined for
  // Cuboh-only users, and cuboh- URLs don't resolve to a CN location.
  const cubohCompany = isDisabledDueToCubohLocation
    ? companies?.[(restaurantId || '').replace('cuboh-', '')]
    : Object.values(companies || {}).find(
        company => String(company.chownow_restaurant_id) === String(location?.id),
      );
  const merchantsForLocation = Object.values(merchants || {}).filter(
    merchant => cubohCompany && Number(merchant.companyID) === Number(cubohCompany.id),
  );

  // ChowNow locations have product_plan_group (HUB = multi-merchant); Cuboh-only
  // locations don't, so fall back to merchant count
  const isSingleMerchantType = location?.product_plan_group
    ? location.product_plan_group !== PRICING_PLANS.hub
    : merchantsForLocation.length === 1;

  const value = {
    headquarter,
    headquarterLocations,
    isHqLoading,
    location: location
      ? {
          // merge the location from the hq locations endpoint
          // for fields like the go_live_requirements %
          ...headquarterLocations?.find(l => l.id === location.id),
          // importantly, location is splatted second so that fields on the
          // `location` object take precedence
          ...location,
        }
      : undefined,
    isLocationLoading,
    locationStatus: urlLocationStatus,
    isCompanyUnauthorized,
    refreshLocation,
    silentRefreshLocation,
    refreshCompanyLocations,
    isSingleMerchantType,
    cubohCompany,
  };

  return <HeadquarterContext.Provider value={value}>{children}</HeadquarterContext.Provider>;
}

HeadquarterProvider.propTypes = {
  children: PropTypes.node,
};
